import {
	configureDhc,
	createDhcNote,
	deleteDhcNote,
	editDhcNote,
	getDhcNotes,
	getDhcAdminClients,
	getDhcClients,
	getDhcDetails,
	getDhcExpertiseList,
	getDhcList,
	getDhcOpenUsers,
	getDhcPayouts,
	getDhcPayoutApproval,
	getDhcQaList,
	getDhcSwapAssignClients,
	getDhcs,
	exportDhcs,
	batchRatePeriods,
	ratePeriod,
} from "@/api/dhcs";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ConfigureDhcPayload } from "@/modules/dhcs/types";
import { useRef } from "react";
import { downloadBlob } from "@/shared/utils/csv";
import { showNotifications } from "@/shared/utils";

export const useGetDhcs = (filter: Record<string, string | boolean | number>) => {
	return useQuery({
		queryKey: ["dhcs", filter],
		queryFn: () => getDhcs(filter),
	});
};

export const useGetDhcDetails = (dhcId?: string) => {
	return useQuery({
		queryKey: ["dhc-details", dhcId],
		queryFn: () => getDhcDetails(dhcId ?? ""),
		enabled: Boolean(dhcId),
	});
};

export const useGetDhcNotes = (dhcId?: string) => {
	return useQuery({
		queryKey: ["dhc-notes", dhcId],
		queryFn: () => getDhcNotes(dhcId ?? ""),
		enabled: Boolean(dhcId),
	});
};

export const useGetDhcList = (keywords?: string) => {
	return useQuery({
		queryKey: ["dhc-list", keywords ?? ""],
		queryFn: () => getDhcList(keywords),
	});
};

export const useGetDhcListDropdown = (keywords?: string) => {
	return useQuery({
		queryKey: ["dhc-list-dropdown", keywords ?? ""],
		queryFn: () => getDhcList(keywords),
	});
};

export const useGetDhcExpertiseList = () => {
	return useQuery({
		queryKey: ["dhc-expertise-list"],
		queryFn: () => getDhcExpertiseList(),
	});
};

export const useGetDhcQaList = () => {
	return useQuery({
		queryKey: ["dhc-qa-list"],
		queryFn: () => getDhcQaList(),
	});
};

export const useConfigureDhc = () => {
	return useMutation({
		mutationFn: (payload: ConfigureDhcPayload) => configureDhc(payload),
	});
};

export const useCreateDhcNote = () => {
	return useMutation({
		mutationFn: (payload: { dhcId: number; note: string }) => createDhcNote(payload.dhcId, payload.note),
	});
};

export const useDeleteDhcNote = () => {
	return useMutation({
		mutationFn: (noteId: number) => deleteDhcNote(noteId),
	});
};

export const useEditDhcNote = () => {
	return useMutation({
		mutationFn: (payload: { noteId: number; note: string }) => editDhcNote(payload.noteId, payload.note),
	});
};

export const useGetDhcClients = (dhcId: string) => {
	return useQuery({
		queryKey: ["dhc-clients", dhcId],
		queryFn: () => getDhcClients(dhcId),
	});
};

export const useGetDhcAdminClients = (dhcId: string, page = 1, limit = 20, locale = "en", enabled = true) => {
	return useQuery({
		queryKey: ["dhc-admin-clients", dhcId, page, limit, locale],
		queryFn: () => getDhcAdminClients(dhcId, page, limit, locale),
		enabled,
	});
};

export const useGetDhcSwapAssignClients = (dhcId: string, page = 1, limit = 20, locale = "en", enabled = true) => {
	return useQuery({
		queryKey: ["dhc-swap-assign-clients", dhcId, page, limit, locale],
		queryFn: () => getDhcSwapAssignClients(dhcId, page, limit, locale),
		enabled,
	});
};

export const useGetDhcOpenUsers = (page = 1, limit = 20, locale = "en", enabled = true) => {
	return useQuery({
		queryKey: ["dhc-open-users", page, limit, locale],
		queryFn: () => getDhcOpenUsers(page, limit, locale),
		enabled,
	});
};

export const useGetDhcPayouts = (dhcId: string) => {
	return useQuery({
		queryKey: ["dhc-payouts", dhcId],
		queryFn: () => getDhcPayouts(dhcId),
	});
};

export const useGetDhcPayoutApproval = (dhcIds: string[], locale = "en", enabled = true) => {
	return useQuery({
		queryKey: ["dhc-payout-approval", dhcIds, locale],
		queryFn: () => getDhcPayoutApproval(dhcIds, locale),
		enabled: enabled && Boolean(dhcIds.length),
	});
};

export const useBatchRatePeriods = () => {
	return useMutation({
		mutationFn: (periods: { id: number; qaRating: number; qaComment: string }[]) => batchRatePeriods(periods),
	});
};

export const useRatePeriod = () => {
	return useMutation({
		mutationFn: ({ id, qaRating, qaComment }: { id: number; qaRating: number; qaComment: string }) =>
			ratePeriod(id, qaRating, qaComment),
	});
};

export const useExportDhcs = () => {
	const abortControllerRef = useRef<AbortController | null>(null);
	const formatRef = useRef<"csv" | "xlsx">("csv");

	const { isPending, mutate } = useMutation({
		mutationFn: ({
			format,
			ids,
			signal,
		}: {
			format: "csv" | "xlsx";
			ids: Array<number | string>;
			signal: AbortSignal;
		}) => exportDhcs(format, ids, signal),
		onSuccess: (blob) => {
			downloadBlob(blob, `dhcs-export.${formatRef.current}`);
		},
		onError: (error: unknown) => {
			if (error instanceof Error && error.name === "CanceledError") {
				return;
			}
			showNotifications({ title: "Export Failed", message: "Failed to export DHC data.", successNotification: false });
		},
	});

	const handleExport = (format: "csv" | "xlsx", ids: Array<number | string> = []) => {
		formatRef.current = format;
		const controller = new AbortController();
		abortControllerRef.current = controller;
		mutate({ format, ids, signal: controller.signal });
	};

	const handleCancelExport = () => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
	};

	return {
		exportFormat: (isPending ? formatRef.current : undefined) as "csv" | "xlsx" | undefined,
		handleExport,
		handleCancelExport,
	};
};
